import { useMemo, useState, type ReactNode } from 'react';
import { BrowserRouter, MemoryRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BRAND_STAFF, SprocketThemeProvider, ToastProvider } from '@platform/design-system';
import {
  ApiProvider,
  RequireRoute,
  RequireSession,
  SessionProvider,
  useSession,
} from '@platform/app-kit';
import type { Runtime } from './bootstrap.ts';
import { AccountPage } from './pages/account-page.tsx';
import { ForgotPasswordPage } from './pages/forgot-password-page.tsx';
import { LoginPage } from './pages/login-page.tsx';
import { NotFoundPage } from './pages/not-found-page.tsx';
import { PackagesPage } from './pages/packages-page.tsx';
import { TenantDetailPage } from './pages/tenant-detail-page.tsx';
import { TenantsPage } from './pages/tenants-page.tsx';
import { NoticesPage } from './pages/notices-page.tsx';
import { UsagePage } from './pages/usage-page.tsx';
import { AdminShell } from './shell/admin-shell.tsx';
import {
  ANNOUNCEMENTS_ROUTE,
  PACKAGES_ROUTE,
  TENANTS_ROUTE,
  TENANT_ROUTE,
  USAGE_ROUTE,
} from './shell/routes.ts';

function ApiBoundary({ runtime, children }: { runtime: Runtime; children: ReactNode }) {
  const { getAccessToken, getPrincipal } = useSession();
  const api = useMemo(
    () =>
      runtime.createApi({
        getAccessToken,
        getPrincipal,
        getTenantId: () => getPrincipal()?.tenantId ?? null,
      }),
    [runtime, getAccessToken, getPrincipal],
  );
  return <ApiProvider value={api}>{children}</ApiProvider>;
}

export function AppRoutes() {
  const { key } = useLocation();
  return (
    <Routes>
      <Route path="/login" element={<LoginPage key={key} />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route
        element={
          <RequireSession>
            <AdminShell />
          </RequireSession>
        }
      >
        <Route index element={<Navigate to="/tenants" replace />} />
        <Route
          path="/tenants"
          element={
            <RequireRoute route={TENANTS_ROUTE} homeHref="/tenants">
              <TenantsPage />
            </RequireRoute>
          }
        />
        <Route
          path="/tenants/:id"
          element={
            <RequireRoute route={TENANT_ROUTE} homeHref="/tenants">
              <TenantDetailPage />
            </RequireRoute>
          }
        />
        <Route
          path="/packages"
          element={
            <RequireRoute route={PACKAGES_ROUTE} homeHref="/tenants">
              <PackagesPage />
            </RequireRoute>
          }
        />
        <Route
          path="/usage"
          element={
            <RequireRoute route={USAGE_ROUTE} homeHref="/tenants">
              <UsagePage />
            </RequireRoute>
          }
        />
        <Route
          path="/notices"
          element={
            <RequireRoute route={ANNOUNCEMENTS_ROUTE} homeHref="/tenants">
              <NoticesPage />
            </RequireRoute>
          }
        />
        <Route path="/account" element={<AccountPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export interface AppProps {
  runtime: Runtime;
  initialPath?: string;
  queryClient?: QueryClient;
}

export function App({ runtime, initialPath, queryClient }: AppProps) {
  const [client] = useState(
    () =>
      queryClient ??
      new QueryClient({ defaultOptions: { queries: { retry: false, staleTime: 5_000 } } }),
  );
  const Router = initialPath ? MemoryRouter : BrowserRouter;
  const routerProps = initialPath ? { initialEntries: [initialPath] } : {};
  return (
    <QueryClientProvider client={client}>
      <SessionProvider adapter={runtime.adapter}>
        <SprocketThemeProvider brand={BRAND_STAFF}>
          <ToastProvider>
            <ApiBoundary runtime={runtime}>
              <Router {...routerProps}>
                <AppRoutes />
              </Router>
            </ApiBoundary>
          </ToastProvider>
        </SprocketThemeProvider>
      </SessionProvider>
    </QueryClientProvider>
  );
}
